import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, qs } from '../lib/api';
import { useAuth } from '../auth/AuthProvider';
import { Icon } from './Icon';
import { useToast } from './Toast';
import { useShiftRecorder } from './ShiftRecorder';
import { desktop } from '../lib/desktop';
import { NO_LINE_MESSAGE, usePairedPhone } from '../lib/phone';
import { PhoneBar } from '../pages/callcenter/Console';

/**
 * لوحة اتصالٍ راسية — الكونسول يحضر إلى الصفحة لا العكس.
 *
 * زرّ الهاتف في القوائم وملف العميل كان يُبحر إلى مركز الاتصال؛ الآن
 * تنفتح هذه اللوحة في مكانها بهيئة المُرسِل الراسي نفسها: اتصالٌ يفتح
 * من جهاز الموظف (`tel:` — الهاتف المقترن) وتُسجَّل المكالمة في النظام،
 * ثم تُنهى هنا بنتيجتها وملاحظاتها كما في الكونسول سواء — والمسارات
 * والنتائج هي ذاتها فالسجل واحد.
 *
 * ## والتسجيل الإلزامي يلحقها لا يحيلها
 *
 * مسجّل الوردية استُخرج من الكونسول مشتركًا: حين تُلزم الشركة
 * بالتسجيل، أولُ «اتصال» من اللوحة يطلب إذن مشاركة الشاشة بصوت
 * النظام — إيماءةُ النقر هي ما يشترطه المتصفح — ثم تُسجَّل المكالمة
 * وتُرفع إلى ملفها كما لو بدأت من الكونسول سواء. ومن رفض الإذن لا
 * تبدأ مكالمته، فالسياسة سياسة.
 */

interface ActiveCall {
  id: string;
  phone: string;
  startedAt: string;
  /** لحظةُ رفعِ السمّاعة — تُختم من الشاشة حين يُبلّغ الخطُّ «Talking». */
  answeredAt: string | null;
  lead: { name: string } | null;
  account: { name: string } | null;
  contact: { name: string } | null;
}

interface Disposition {
  id: string;
  label: string;
  kind: string;
  suggestsFollowUp: boolean;
  isActive: boolean;
}

/** أرقام عربية وفارسية تُطبَّع لاتينية — كما في الكونسول سواء. */
function toAsciiDigits(raw: string): string {
  let out = '';
  for (const ch of raw) {
    const code = ch.codePointAt(0)!;
    if (code >= 0x0660 && code <= 0x0669) out += String(code - 0x0660);
    else if (code >= 0x06f0 && code <= 0x06f9) out += String(code - 0x06f0);
    else if (/[0-9+\s()-]/.test(ch)) out += ch;
  }
  return out;
}

/**
 * مدةٌ حيّة تنبض كل ثانية — من الردِّ لا من الطلب.
 *
 * وما قبلَ الردِّ رنينٌ يُقال باسمه، فلا يقرأ الموظّفُ دقيقةً وهو لم
 * يتكلّم بعدُ إلّا عشرين ثانية. ومن لا خطَّ له يُبلّغ الردَّ يُقاس من
 * الطلبِ كما كان — لا خبرَ عنده أدقّ.
 */
function LiveTimer({
  since,
  answeredAt,
  ringing = false,
}: {
  since: string;
  answeredAt?: string | null;
  ringing?: boolean;
}) {
  const [, tick] = useState(0);
  useEffect(() => {
    const t = window.setInterval(() => tick((x) => x + 1), 1000);
    return () => window.clearInterval(t);
  }, []);
  if (ringing && !answeredAt) {
    return <span className="tiny mute">يرنّ…</span>;
  }
  const sec = Math.max(
    0,
    Math.floor((Date.now() - new Date(answeredAt ?? since).getTime()) / 1000),
  );
  const mm = String(Math.floor(sec / 60)).padStart(2, '0');
  const ss = String(sec % 60).padStart(2, '0');
  return (
    <span className="num" dir="ltr">
      {mm}:{ss}
    </span>
  );
}

export function QuickCallModal({
  clientName,
  phone: initialPhone,
  link,
  skipRecording = false,
  onClose,
}: {
  clientName: string;
  phone: string;
  /** السجل الذي تُنسب إليه المكالمة فتدخل تاريخه. */
  link?: {
    leadId?: string | null;
    accountId?: string | null;
    contactId?: string | null;
    candidateId?: string | null;
  };
  /**
   * إعفاءٌ من سياسة تسجيل الوردية.
   *
   * السياسةُ سياسةُ مركز الاتصال، أُعلنت لموظّفيه ووُقّعت منهم. ومكالمةُ
   * فرزٍ يجريها موظّف الموارد البشرية ليست وردية مركز اتصال — فأوّل ما
   * يراه عند الضغط لا يجوز أن يكون طلبَ مشاركة شاشةٍ لم يُخبَر به.
   */
  skipRecording?: boolean;
  onClose: () => void;
}) {
  const { session } = useAuth();
  const user = session!.user;
  const toast = useToast();
  const queryClient = useQueryClient();

  const [phone, setPhone] = useState(toAsciiDigits(initialPhone));
  const [wrapping, setWrapping] = useState(false);
  const digits = phone.replace(/\D/g, '');

  /* مسجّل الوردية المشترك — السياسة والجلسة والالتقاط كما في الكونسول. */
  const recorder = useShiftRecorder();
  const mustRecord =
    !skipRecording && (recorder.policy.callRecording || recorder.policy.mode !== 'OFF');

  /* مكالمتي الجارية — مفتاح الكونسول نفسه فالكاش واحد بينهما. */
  const active = useQuery({
    queryKey: ['cc', 'active', user.id],
    queryFn: async () => {
      const page = await api<{ items: ActiveCall[] }>(
        `/callcenter/calls${qs({ agentId: user.id, status: 'IN_PROGRESS', pageSize: 1 })}`,
      );
      return page.items[0] ?? null;
    },
    refetchInterval: 15_000,
  });
  const call = active.data ?? null;

  /* الهاتف المقترن (تطبيق سطح المكتب) — كما في الكونسول سواء. */
  const paired = usePairedPhone(!!call);

  /*
   * الشريطُ يبدأ مع الردّ لا مع الطلب.
   *
   * كان يبدأ لحظةَ ظهورِ المكالمة في النظام — أي مع الضغطةِ على «اتصال»
   * — فيلتهمُ الرنينَ كلَّه قبل أن يردَّ أحد، وقد لا يردّ. فما دام الخطُّ
   * يُخبرنا بحاله، فالبدءُ عند «Active»: حين يُرفع السمّاعةُ فعلًا.
   * ومن لا خطَّ مقترنًا له يبقى على ظهورِ المكالمة — لا خبرَ عنده أدقّ.
   */
  useEffect(() => {
    if (!call || !recorder.session) return;
    if (paired.available && !paired.answered) return;
    recorder.beginCall();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [call?.id, recorder.session, paired.available, paired.answered]);

  /* ولحظةُ الردِّ تُختم في الخادم — بها يعدُّ المؤشّرُ وتُقاس المدّة. */
  useEffect(() => {
    if (!call || call.answeredAt || !paired.answered) return;
    void api(`/callcenter/calls/${call.id}/answered`, { method: 'PATCH' })
      .then(() => queryClient.invalidateQueries({ queryKey: ['cc'] }))
      .catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [call?.id, call?.answeredAt, paired.answered]);

  useEffect(() => {
    if (paired.endedFromPhone && call && !wrapping) {
      setWrapping(true);
      toast('أُقفلت المكالمة من الهاتف — سجّل نتيجتها');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paired.endedFromPhone]);

  /*
   * والشريطُ يُقفل مع الخطّ لا مع النتيجة.
   *
   * وأبوابُ الإنهاءِ ثلاثة — إقفالٌ من الشريط، وإنهاءٌ باليد، وإقفالٌ من
   * الهاتف — وكلُّها تمرّ بـ«التلخيص»، فالإيقافُ عنده يكفيها جميعًا.
   */
  useEffect(() => {
    if (wrapping) void recorder.haltCall();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wrapping]);

  const start = useMutation({
    mutationFn: () =>
      api<ActiveCall>('/callcenter/calls', {
        method: 'POST',
        body: {
          direction: 'OUTBOUND',
          phone: phone.trim(),
          leadId: link?.leadId ?? undefined,
          accountId: link?.accountId ?? undefined,
          contactId: link?.contactId ?? undefined,
          candidateId: link?.candidateId ?? undefined,
        },
      }),
    onSuccess: async () => {
      void queryClient.invalidateQueries({ queryKey: ['cc'] });
      /*
       * الصوت من جهاز الموظف: الهاتفُ المقترن مباشرةً من تطبيق سطح المكتب —
       * ولا يُفتح تطبيقُ الاتصال بديلًا، فنافذتُه هي ما جئنا نُغني عنه.
       */
      if (paired.available) {
        const r = await paired.dial(digits, clientName);
        if (!r.ok)
          toast(r.error === 'no-line' ? NO_LINE_MESSAGE : 'تعذّر الاتصال من الهاتف — أعد المحاولة');
        return;
      }
      /* وفي المتصفّح الرابط يفتح تطبيقَ الاتصال. */
      const a = document.createElement('a');
      a.href = `tel:${digits}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر بدء المكالمة'),
  });

  /*
   * التسجيل الملزم لا يحجز الاتصال بزرٍّ منفصل: ضغطةُ «اتصال» — وهي
   * إيماءة المستخدم التي يشترطها المتصفح — تفتح الجلسة أولًا، ومن رفض
   * الإذن لا تبدأ مكالمته.
   */
  const dial = async () => {
    if (digits.length < 7 || start.isPending || call) return;
    /* في تطبيق سطح المكتب: لا خطَّ فلا مكالمةٌ تُفتح في النظام ثمّ تُلغى. */
    if (paired.available && !(await paired.ensureLine())) {
      toast(NO_LINE_MESSAGE);
      return;
    }
    if (mustRecord && !recorder.session) {
      const ok = await recorder.startShift();
      if (!ok) {
        toast('التسجيل مُلزَم في شركتك — امنح إذن المشاركة لبدء المكالمة');
        return;
      }
    }
    start.mutate();
  };

  const who = call ? (call.lead?.name ?? call.account?.name ?? call.contact?.name) : null;

  return (
    <div className="qm-composer qm-call" role="dialog" aria-label="اتصال">
      <div className="qm-head">
        <Icon name="phone" size={18} />
        <span className="qm-title">{call ? (who ?? call.phone) : clientName}</span>
        {call && (
          <LiveTimer
            since={call.startedAt}
            answeredAt={call.answeredAt}
            ringing={paired.available}
          />
        )}
        <button className="qm-x" onClick={onClose} aria-label="إغلاق">
          <Icon name="x" size={15} />
        </button>
      </div>

      <div className="qm-body">
        {call ? (
          wrapping ? (
            <WrapUp
              call={call}
              onDone={(status) => {
                setWrapping(false);
                /* الخطُّ ما زال مفتوحًا على الهاتف بعد تسجيل النتيجة: يُقفل. */
                if (paired.call) void paired.end();
                /* تسجيل المكالمة يُقفل ويُرفع إلى ملفها — كالكونسول سواء. */
                void recorder.finishCall(call.id, status);
                void queryClient.invalidateQueries({ queryKey: ['cc'] });
                void queryClient.invalidateQueries({ queryKey: ['activities'] });
                void queryClient.invalidateQueries({ queryKey: ['timeline'] });
              }}
            />
          ) : (
            <>
              <p className="small">
                مكالمة جارية مع <strong>{who ?? call.phone}</strong> — الصوت على جهازك.
              </p>
              {paired.available && (
                <div className="mt-3">
                  <PhoneBar paired={paired} onHangUp={() => setWrapping(true)} compact />
                </div>
              )}
              <div className="row mt-3" style={{ gap: 'var(--sp-2)', flexWrap: 'wrap' }}>
                <button className="btn btn-primary btn-sm" onClick={() => setWrapping(true)}>
                  <Icon name="check" size={14} />
                  إنهاء وتسجيل النتيجة
                </button>
                <Link className="btn btn-sm" to="/callcenter">
                  افتح في الكونسول
                </Link>
              </div>
            </>
          )
        ) : (
          <>
            <input
              className="input num"
              dir="ltr"
              value={phone}
              onChange={(e) => setPhone(toAsciiDigits(e.target.value))}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void dial();
              }}
              aria-label="الرقم"
            />
            <span className="tiny mute" style={{ display: 'block', marginTop: 6 }}>
              {paired.ready
                ? `الاتصال من هاتفك المقترن (${paired.line?.name}) مباشرةً من الضغطة، وتُسجَّل المكالمة في سجل العميل وتُنهى هنا بنتيجتها.`
                : 'الاتصال يفتح من جهازك — هاتف الشركة المقترن — وتُسجَّل المكالمة في سجل العميل، وتُنهى هنا بنتيجتها.'}
              {/*
                والإذنُ ليس واحدًا في البابين: التطبيقُ يلتقطُ صوتَ النظام
                من مخرجِه ويعطي نافذتَه صورةً، فلا يسألُ شيئًا. والمتصفّحُ
                لا يعطي صوتَ النظام إلّا خلفَ نافذةِ المشاركة. فمن قيل له
                «سيُطلب إذن» وهو في التطبيق انتظر سؤالًا لا يجيء، ومن قيل
                له ذلك في المتصفّح حُقَّ له أن يعرف أنّ للتطبيقِ بابًا أهدأ.
              */}
              {mustRecord &&
                !recorder.session &&
                (desktop
                  ? ' التسجيل مُلزَم: يبدأ من التطبيق تلقائيًّا — بلا إذنٍ ولا مشاركةِ شاشة.'
                  : ' التسجيل مُلزَم: أول اتصال سيطلب إذن مشاركة الشاشة بصوت النظام مرةً واحدة — ومن تطبيق سطح المكتب يبدأ بلا إذن.')}
            </span>
          </>
        )}
      </div>

      {!call && (
        <div className="qm-foot">
          <button
            className="btn btn-primary qm-send"
            disabled={digits.length < 7 || start.isPending}
            onClick={() => void dial()}
          >
            <Icon name="phone" size={15} />
            {start.isPending ? 'جارٍ البدء…' : 'اتصال'}
          </button>
        </div>
      )}
    </div>
  );
}

/** نتيجة المكالمة — الحقول والمسارات هي حقول الكونسول ومساراته سواء. */
function WrapUp({ call, onDone }: { call: ActiveCall; onDone: (status: string) => void }) {
  const toast = useToast();
  const [dispositionId, setDispositionId] = useState('');
  const [note, setNote] = useState('');
  const [followUp, setFollowUp] = useState('');
  const [error, setError] = useState<string | null>(null);

  const dispositions = useQuery({
    queryKey: ['cc', 'dispositions'],
    queryFn: () => api<{ items: Disposition[] }>('/callcenter/dispositions'),
    staleTime: 5 * 60_000,
  });
  const items = (dispositions.data?.items ?? []).filter((d) => d.isActive);
  const chosen = items.find((d) => d.id === dispositionId);

  /* نتيجةٌ تقترح متابعةً تملأ الموعد بثلاثة أيام — ويبقى قابلًا للمسح. */
  useEffect(() => {
    if (chosen?.suggestsFollowUp && !followUp) {
      const d = new Date(Date.now() + 3 * 86_400_000);
      d.setMinutes(0, 0, 0);
      setFollowUp(d.toISOString().slice(0, 16));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispositionId]);

  const end = useMutation({
    mutationFn: (status: 'COMPLETED' | 'NO_ANSWER' | 'CANCELLED') =>
      api(`/callcenter/calls/${call.id}/end`, {
        method: 'PATCH',
        body: {
          status,
          dispositionId: status === 'COMPLETED' ? dispositionId || undefined : undefined,
          note: note.trim() || undefined,
          followUpAt: followUp ? new Date(followUp).toISOString() : undefined,
        },
      }),
    onSuccess: (_res, status) => {
      toast('سُجّلت المكالمة في ملف العميل');
      onDone(status);
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'تعذّر الإنهاء.'),
  });

  return (
    <>
      {error && <div className="alert alert-danger mb-3">{error}</div>}

      <div className="cc-disp-grid">
        {items.map((d) => (
          <button
            key={d.id}
            type="button"
            className={`cc-disp${dispositionId === d.id ? ' on' : ''}`}
            data-kind={d.kind}
            onClick={() => setDispositionId(d.id)}
            aria-pressed={dispositionId === d.id}
          >
            {d.label}
          </button>
        ))}
      </div>

      <label className="field mt-3">
        <span className="label">ملاحظات — تدخل سجلّ التعامل</span>
        <textarea
          className="textarea"
          rows={2}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="ما قاله العميل، وما وُعد به…"
        />
      </label>

      <label className="field">
        <span className="label">متابعة (اختياري) — تُنشأ مهمةً بموعدها</span>
        <input
          className="input"
          type="datetime-local"
          value={followUp}
          onChange={(e) => setFollowUp(e.target.value)}
        />
      </label>

      <div className="row mt-3" style={{ gap: 'var(--sp-2)', flexWrap: 'wrap' }}>
        <button
          className="btn btn-primary btn-sm"
          disabled={!dispositionId || end.isPending}
          onClick={() => end.mutate('COMPLETED')}
        >
          تمّت — سجّل
        </button>
        <button
          className="btn btn-sm"
          disabled={end.isPending}
          onClick={() => end.mutate('NO_ANSWER')}
        >
          لم يُرَدّ
        </button>
        {/* الملغاة لا تدخل سجل التعامل — نقرة خاطئة ليست تاريخ تواصل. */}
        <button
          className="btn btn-sm btn-ghost"
          disabled={end.isPending}
          onClick={() => end.mutate('CANCELLED')}
        >
          إلغاء (خطأ في الطلب)
        </button>
      </div>
    </>
  );
}
